import React, { useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  Card,
  Typography,
  useTheme,
  Autocomplete,
  IconButton,
} from "@mui/material";
import {
  useCreateNewsletterEmailClientMutation,
  useGetEmailTemplatesQuery,
} from "../../../Api/attoDeskApi";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { uploadAttachments } from "../../../Components/UploadFile/upload";
import { widthofReactQuill } from "../../../Core/Enum/enum";
import SendIcon from "@mui/icons-material/Send";
import {
  EmailTemplateOption,
  IEmailTemplet,
} from "../../../Api/Interface/api.interface";
import { Delete } from "@mui/icons-material";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import { useLocation } from "react-router-dom";

interface FormValues {
  subject: string;
  message: string;
  clientIds: number[];
  attachments: string[];
}

interface FormValuesSms {
  message: string;
  clientIds: number[];
}

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const ids = query.get("ids")?.split(",").map(Number) ?? [];
  const [selectedOption, setSelectedOption] =
    useState<EmailTemplateOption | null>(null);
  const theme = useTheme();
  const [attachments, setAttachments] = useState<File[]>([]);

  const [newEmail, { isLoading: createEmaliRecordLoading }] =
    useCreateNewsletterEmailClientMutation();

  const { data: userroleData, isLoading: userroleIsLoading } =
    useGetEmailTemplatesQuery({
      searchText: "",
    });

  const userroleList = useMemo(() => {
    return userroleData?.data as IEmailTemplet[];
  }, [userroleData?.data]);

  const userRoleOptions: EmailTemplateOption[] = useMemo(() => {
    if (!Array.isArray(userroleList)) return [];
    return userroleList.map((cust) => ({
      label: toProperCase(cust.template_name),
      value: cust.id,
      subject: cust.subject,
      message: cust.message,
      attachments: cust.attachments,
    }));
  }, [userroleList]);

  const formikEmail = useFormik<FormValues>({
    initialValues: {
      clientIds: ids,
      subject: "",
      message: "",
      attachments: [],
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        // const uploadedUrls: string[] = [];
        // for (const file of attachments) {
        //   const url = await uploadAttachments(file);
        //   if (url) uploadedUrls.push(url);
        // }

        const uploadedUrls: string[] = [...(values.attachments || [])];

        for (const file of attachments) {
          try {
            const url = await uploadAttachments(file); // your upload function
            if (url) {
              uploadedUrls.push(url);
            }
          } catch (error) {
            showMessage(`Failed to upload file ${file.name}: ${error}`);
          }
        }

        console.log("Uploaded URLs:", uploadedUrls);

        const payload = {
          clientIds: values.clientIds,
          subject: values.subject,
          message: values.message,
          attachments: uploadedUrls,
        };

        const addCompanyResponse = await newEmail(payload).unwrap();

        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          // resetForm();
          navigate(`/client/newsletters/view/${addCompanyResponse.data.id}`);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleResetSetValueFormik = () => {
    formikEmail.setFieldValue("subject", "");
    formikEmail.setFieldValue("message", "");
    setAttachments([]);
  };

  const formValidEmail = useMemo(() => {
    return (
      formikEmail.values.subject &&
      formikEmail.values.message &&
      formikEmail.values.clientIds.length > 0
    );
  }, [formikEmail]);

  const handleAutocompleteChange = (
    event: React.SyntheticEvent,
    newValue: EmailTemplateOption | null
  ) => {
    setSelectedOption(newValue);

    if (newValue) {
      formikEmail.setFieldValue("subject", newValue.subject || "");
      formikEmail.setFieldValue("message", newValue.message || "");
      formikEmail.setFieldValue("attachments", newValue.attachments || []);
    } else {
      formikEmail.setFieldValue("subject", "");
      formikEmail.setFieldValue("message", "");
      formikEmail.setFieldValue("attachments", []);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setAttachments((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <form onSubmit={formikEmail.handleSubmit}>
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={8}
                      md={6}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        E-mail
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      lg={4}
                      md={6}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Autocomplete
                        options={userRoleOptions}
                        loading={userroleIsLoading}
                        value={selectedOption}
                        getOptionLabel={(option) => option.label}
                        onChange={handleAutocompleteChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            InputLabelProps={{ shrink: true }}
                            placeholder="Template Name  .."
                          />
                        )}
                        sx={{ flexGrow: 1, width: "100%" }}
                        size="small"
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Subject{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Subject"
                            size="small"
                            multiline
                            rows={3}
                            {...formikEmail.getFieldProps("subject")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12} pb={5}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                            Message{" "}
                            <span className={classes.requiedfield}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <ReactQuill
                            value={formikEmail.values.message}
                            onChange={(value) =>
                              formikEmail.setFieldValue("message", value)
                            }
                            placeholder="Type your message..."
                            theme="snow"
                            style={{ height: widthofReactQuill["400px"] }}
                            modules={{
                              toolbar: [
                                [{ font: [] }],
                                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                ["bold", "italic", "underline", "strike"],
                                [{ color: [] }, { background: [] }],
                                [{ script: "sub" }, { script: "super" }],
                                [{ list: "ordered" }, { list: "bullet" }],
                                [{ indent: "-1" }, { indent: "+1" }],
                                [{ direction: "rtl" }],
                                [{ align: [] }],
                                ["blockquote", "code-block"],
                                ["link", "image", "video"],
                                ["clean"],
                              ],
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Typography variant="subtitle1">Attachments</Typography>
                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*,application/pdf"
                        onChange={handleFileChange}
                      />
                    </Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid container spacing={2}>
                        <Grid item>
                          {formikEmail.values?.attachments &&
                            formikEmail.values.attachments.length > 0 && (
                              <>
                                <Grid container spacing={2}>
                                  {formikEmail.values.attachments.map(
                                    (file: string, index: number) => {
                                      const isImage =
                                        /\.(jpeg|jpg|png|gif|webp)$/i.test(
                                          file
                                        );
                                      const isPdf = /\.pdf$/i.test(file);
                                      const isVideo = /\.mp4$/i.test(file);

                                      const handleDelete = () => {
                                        const updated =
                                          formikEmail.values.attachments.filter(
                                            (_, i) => i !== index
                                          );
                                        formikEmail.setFieldValue(
                                          "attachments",
                                          updated
                                        );
                                      };

                                      return (
                                        <Grid item key={index}>
                                          <div
                                            style={{
                                              position: "relative",
                                              border: "1px solid #ccc",
                                              borderRadius: 8,
                                              width: 200,
                                              height: 200,
                                              overflow: "hidden",
                                            }}
                                          >
                                            <IconButton
                                              size="small"
                                              onClick={handleDelete}
                                              sx={{
                                                position: "absolute",
                                                top: 4,
                                                right: 4,
                                                zIndex: 2,
                                                backgroundColor:
                                                  "rgba(255,255,255,0.8)",
                                                "&:hover": {
                                                  backgroundColor:
                                                    "rgba(255,0,0,0.2)",
                                                },
                                              }}
                                            >
                                              <Delete fontSize="small" />
                                            </IconButton>

                                            <a
                                              href={file}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                cursor: "pointer",
                                                display: "block",
                                                height: "100%",
                                              }}
                                            >
                                              {isImage && (
                                                <img
                                                  src={file}
                                                  alt={`attachment-${index}`}
                                                  style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                  }}
                                                />
                                              )}
                                              {isPdf && (
                                                <object
                                                  data={file}
                                                  type="application/pdf"
                                                  width="100%"
                                                  height="100%"
                                                ></object>
                                              )}
                                              {isVideo && (
                                                <video
                                                  src={file}
                                                  controls
                                                  style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                  }}
                                                />
                                              )}
                                              {!isImage &&
                                                !isPdf &&
                                                !isVideo && (
                                                  <Typography
                                                    color="primary"
                                                    sx={{
                                                      textAlign: "center",
                                                      mt: 8,
                                                      wordBreak: "break-all",
                                                    }}
                                                  >
                                                    {file.split("/").pop()}
                                                  </Typography>
                                                )}
                                            </a>
                                          </div>
                                        </Grid>
                                      );
                                    }
                                  )}
                                </Grid>
                              </>
                            )}
                        </Grid>
                        <Grid item>
                          <Grid container spacing={2}>
                            {attachments.map((file, index) => {
                              const fileUrl = URL.createObjectURL(file);
                              const isImage =
                                /\.(jpeg|jpg|png|gif|webp)$/i.test(file.name);
                              const isPdf = /\.pdf$/i.test(file.name);
                              const isVideo = /\.mp4$/i.test(file.name);

                              return (
                                <Grid item key={index}>
                                  <div
                                    style={{
                                      position: "relative",
                                      border: "1px solid #ccc",
                                      borderRadius: 8,
                                      padding: 4,
                                      width: 200,
                                      height: 200,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      overflow: "hidden",
                                      backgroundColor: "#fafafa",
                                    }}
                                  >
                                    {/* Delete button */}
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveFile(index)}
                                      sx={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        backgroundColor:
                                          "rgba(255,255,255,0.8)",
                                      }}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>

                                    {/* File previews */}
                                    {isImage && (
                                      <img
                                        src={fileUrl}
                                        alt={file.name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    )}
                                    {isPdf && (
                                      <object
                                        data={fileUrl}
                                        type="application/pdf"
                                        width="100%"
                                        height="100%"
                                      >
                                        <Typography
                                          variant="body2"
                                          color="textSecondary"
                                        >
                                          PDF Preview Not Available
                                        </Typography>
                                      </object>
                                    )}
                                    {isVideo && (
                                      <video
                                        src={fileUrl}
                                        controls
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    )}
                                    {!isImage && !isPdf && !isVideo && (
                                      <Typography
                                        variant="body2"
                                        color="primary"
                                        sx={{ textAlign: "center" }}
                                      >
                                        {file.name}
                                      </Typography>
                                    )}
                                  </div>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12} pt={5}>
                      <Grid
                        container
                        spacing={2}
                        justifyContent={"space-between"}
                      >
                        <Grid
                          item
                          lg={4}
                          md={6}
                          sm={12}
                          xs={12}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        ></Grid>
                        <Grid
                          item
                          lg={8}
                          md={6}
                          sm={12}
                          xs={12}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={() => handleResetSetValueFormik()}
                          >
                            Reset
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              createEmaliRecordLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : (
                                <SendIcon />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formikEmail.handleSubmit()}
                            disabled={
                              !formValidEmail || createEmaliRecordLoading
                            }
                          >
                            Sent
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
